type RainforestXhrErrorListener = (request: XMLHttpRequest) => void

const listeners = new Set<RainforestXhrErrorListener>()

let originalXhrOpen: typeof XMLHttpRequest.prototype.open | null = null

/**
 * Notifies `listener` whenever any XHR completes with a non-2xx response from
 * rainforestpay.com, by wrapping `XMLHttpRequest.prototype.open` while at least
 * one subscriber is registered. Returns an unsubscribe function suitable as a
 * `useEffect` cleanup.
 *
 * A single shared wrapper is installed when the first subscriber registers and
 * the original `open` is restored when the last one leaves. Ref-counting here —
 * rather than each caller patching the prototype in its own effect — is what
 * prevents the ENG-4102 leak: per-caller wrappers stack on every mount, and
 * per-caller restores can resurrect a stale wrapper when mounts overlap and
 * cleanups run out of order.
 */
export function subscribeToRainforestXhrErrors(listener: RainforestXhrErrorListener): () => void {
    listeners.add(listener)

    if (listeners.size === 1) {
        installXhrOpenWrapper()
    }

    return () => {
        listeners.delete(listener)

        if (listeners.size === 0 && originalXhrOpen !== null) {
            XMLHttpRequest.prototype.open = originalXhrOpen
            originalXhrOpen = null
        }
    }
}

function installXhrOpenWrapper(): void {
    const original = XMLHttpRequest.prototype.open

    originalXhrOpen = original

    XMLHttpRequest.prototype.open = function (method: string, url: string | URL, async: boolean = true, username?: string | null, password?: string | null) {
        this.addEventListener('load', function () {
            if (this.status < 200 || this.status >= 300) {
                if (this.responseURL.includes('rainforestpay.com')) {
                    listeners.forEach((listener) => listener(this))
                }
            }
        })

        original.call(this, method, url, async, username, password)
    }
}
